import React, {useEffect, useMemo, useRef, useState} from "react";
import Header from "../../header/header";
import {Company, Persona, PersonaType} from "../../models";
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import {copyPersona, getCompanies, getTags, searchPersonas, updatePersona} from "../../api";
import {
    Autocomplete,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip
} from "@mui/material";
import {PersonaTableRow} from "../components/PersonaTableRow";
import {PersonaSearchInput} from "../components/PersonaSearchInput";
import {searchService} from "../../scripts/search.service";
import {useCurrentUser} from "../../hooks/useCurrentUser";
import {useShowAllCompanies} from "../../hooks/useShowAllCompanies";
import {PersonaCreateDialog} from "../components/PersonaCreateDialog";
import {PersonaViewDialog} from "../components/PersonaViewDialog";
import {useSnackbar} from "notistack";
import {useLocation} from "react-router-dom";

const itemsPerPage = 15;

type SortKey = "createdAt" | "id" | "name" | "profileName" | "type" | "company";

// Company filter values: "all", "unassigned" or a company id
export type CompanyFilter = "all" | "unassigned" | number;
type SortDirection = "asc" | "desc";

export type SearchState = {
    text: string;
    tags: string[];
    spin: string;
    default: boolean;
    reel: boolean;
    gmail: boolean;
    music: boolean;
    article: boolean;
    video: boolean;
    streamer: boolean;
    master: boolean;
    systemGenerated: boolean;
};

export const PersonaListPage: React.FC = () => {
    const {enqueueSnackbar} = useSnackbar();
    const user = useCurrentUser();
    
    const location = useLocation();
    const [group, setGroup] = useState<string | null>(null);

    useEffect(() => {
        if (location.state?.group) {
            setGroup(location.state.group);
        }
    }, [location.state])

    // Data state, personas holds the current page only
    const [personas, setPersonas] = useState<Persona[] | null>(null);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [tags, setTags] = useState<any[] | null>(null);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [fetching, setFetching] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const requestId = useRef(0);

    /// Search state (filtering & pagination)
    const [restore, setRestore] = useState(false);
    const [companyFilter, setCompanyFilter] = useState<CompanyFilter>("all");
    const [showAllCompanies, setShowAllCompanies] = useShowAllCompanies();
    const [page, setPage] = useState(searchService.currentSearchValue?.page ?? 1);
    const [selectedPersonaIds, setSelectedPersonaIds] = useState<number[]>([]);
    const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({
        key: "createdAt",
        direction: "desc",
    });
    const [search, setSearch] = useState<SearchState>({
        text: "",
        tags: [],
        spin: "new",
        default: true,
        reel: true,
        gmail: true,
        music: true,
        article: true,
        video: true,
        streamer: true,
        master: true,
        systemGenerated: false,
    })

    useEffect(() => {
        if (!group) return;

        setSearch(prev => ({
            ...prev,
        default: group === "Default",
        reel: group === "Reel",
        streamer: group === "Streamer",
        gmail: group === "Gmail",
        music: group === "Music",
        article: group === "Article",
        video: group === "Video",
        }));
    }, [group]);

    // Dialog states
    const [persona, setPersona] = useState<Persona | undefined>(undefined);
    const [personaCreateDialogOpen, setPersonaCreateDialogOpen] = useState(false);
    const [personaViewDialogOpen, setPersonaViewDialogOpen] = useState(false);
    const [tagDialogOpen, setTagDialogOpen] = useState(false);
    const [tagsToApply, setTagsToApply] = useState<any[]>([]);

    // Derived state
    const loading = useMemo(() => (!personas || !tags) && !error, [personas, tags, error]);

    // Wait for typing to pause before searching
    const [debouncedText, setDebouncedText] = useState(search.text);
    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedText(search.text), 300);
        return () => clearTimeout(timeout);
    }, [search.text]);

    // Keyed on a string so typing in the search box doesn't count as a type change
    const typesKey = Object.keys(search).filter(
        key =>
            search[key as keyof typeof search] === true &&
            !["text", "tags", "spin", "master", "systemGenerated"].includes(key)
    ).join(",");
    const types = useMemo(() => typesKey ? typesKey.split(",") : [], [typesKey]);

    const tagIds = useMemo(() => (search.tags ?? []).map((t: any) => Number(t.id)), [search.tags]);

    const companyOptions = useMemo(() => companies.map(company => ({id: company.id, name: company.name})), [companies]);

    const selectedPersonas = useMemo(() => personas?.filter(persona => selectedPersonaIds.includes(persona.id)), [personas, selectedPersonaIds]);
    const allSelected = useMemo(() => selectedPersonas?.length === personas?.length && (personas?.length ?? 0) > 0, [selectedPersonas, personas]);

    const tagOptions = useMemo(() => (tags ?? []).map((option: any) => {
        const firstLetter = option.name[0].toUpperCase();
        if (option.important) {
            return {
                firstLetter: " IMPORTANT",
                ...option
            }
        } else {
            return {
                firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
                ...option,
            };
        }
    }).sort((a: any, b: any) => -b.firstLetter.localeCompare(a.firstLetter)), [tags]);

    const updateSearch = <K extends keyof SearchState>(key: K, value: SearchState[K]) => {
        setSearch(prev => ({...prev, [key]: value}));
    };

    function handleSort(key: SortKey) {
        setSort(prev => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    }

    function renderSortLabel(key: SortKey, label: string) {
        return (
            <TableSortLabel
                active={sort.key === key}
                direction={sort.key === key ? sort.direction : "asc"}
                onClick={() => handleSort(key)}
            >
                {label}
            </TableSortLabel>
        );
    }

    // Save search state to local storage
    //useEffect(() => searchService.setSearch({query, types, page}), [query, types, page]);

    // Reset page when the filters or sort change
    useEffect(() => {
        setPage(1);
    }, [debouncedText, types, tagIds, restore, companyFilter, sort]);

    useEffect(() => {
        setSort({
            key: "createdAt",
            direction: search.spin === "new" ? "desc" : "asc",
        });
    }, [search.spin]);

    // Fetch tags once on mount
    useEffect(() => {
        getTags().then(setTags).catch(error => setError(String(error)));
    }, []);

    // Fetch companies on mount, and again when an admin toggles all companies
    useEffect(() => {
        setCompanyFilter("all");
        getCompanies(showAllCompanies).then(setCompanies).catch(error => console.error(error));
    }, [showAllCompanies]);

    // Fetch the current page whenever the filters, sort or page change
    useEffect(() => {
        fetchPersonas();
    }, [debouncedText, types, tagIds, restore, companyFilter, sort, page, showAllCompanies, reloadKey]);

    // Step back if the current page no longer exists, e.g. after deleting its last personas
    useEffect(() => {
        const pageCount = Math.max(1, totalPages);
        if (page > pageCount) setPage(pageCount);
    }, [totalPages, page]);

    // Reset temp persona when dialogs are closed
    useEffect(() => {
        if (!personaViewDialogOpen) {
            setPersona(undefined);
        }
    }, [personaViewDialogOpen]);

    // Reset selected persona ids when the page of personas changes
    useEffect(() => {
        setSelectedPersonaIds([]);
    }, [personas]);

    async function fetchPersonas() {
        // Only the latest request may update state, so slower earlier responses are dropped
        const id = ++requestId.current;
        setError(null);
        setFetching(true);

        try {
            const result = await searchPersonas({
                page,
                pageSize: itemsPerPage,
                text: debouncedText,
                types,
                tagIds,
                deleted: restore,
                company: companyFilter,
                sortKey: sort.key,
                sortDirection: sort.direction,
            });
            if (id !== requestId.current) return;
            setPersonas(result.personas);
            setTotal(result.total);
            setTotalPages(result.totalPages);
        } catch (error) {
            if (id !== requestId.current) return;
            setError(String(error));
        } finally {
            if (id === requestId.current) setFetching(false);
        }
    }

    function fetchData() {
        setReloadKey(prev => prev + 1);
    }

    function handlePersonaNew() {
        setPersona(undefined)
        setPersonaCreateDialogOpen(true)
    }

    function handlePersonaView(persona: Persona) {
        setPersona(persona)
        setPersonaViewDialogOpen(true)
    }

    async function handlePersonaCopy(persona: Persona) {
        enqueueSnackbar("Copying persona", {variant: "info"});

        try {
            await copyPersona(persona.id);
            enqueueSnackbar("Copied", {variant: "success"});
            await fetchData();
        } catch (e) {
            enqueueSnackbar("Something went wrong", {variant: "error"});
            console.error(e)
        }
    }

    function handlePersonaSelect(persona: Persona) {
        if (selectedPersonaIds.includes(persona.id)) {
            setSelectedPersonaIds(selectedPersonaIds.filter(id => id !== persona.id));
        } else {
            setSelectedPersonaIds([...selectedPersonaIds, persona.id]);
        }
    }

    function handleSelectAll() {
        if (allSelected) {
            setSelectedPersonaIds([]);
        } else {
            setSelectedPersonaIds(personas?.map(persona => persona.id) ?? []);
        }
    }

    async function handleDeleteSelectedPersonas() {
        enqueueSnackbar("Deleting selected personas", {variant: "info"});

        try {
            await Promise.all((selectedPersonas ?? []).map(persona => updatePersona(persona.id, {
                status: "deleted"
            })));
            enqueueSnackbar("Selected personas deleted", {variant: "success"});
            setSelectedPersonaIds([]);
            await fetchData();
        } catch (e) {
            enqueueSnackbar("Something went wrong", {variant: "error"});
            console.error(e)
        }
    }

    async function handleRestoreSelectedPersonas() {
        enqueueSnackbar("Restoring selected personas", {variant: "info"});

        try {
            await Promise.all((selectedPersonas ?? []).map(persona => updatePersona(persona.id, {
                status: "active"
            })));
            enqueueSnackbar("Selected personas restored", {variant: "success"});
            setSelectedPersonaIds([]);
            await fetchData();
        } catch (e) {
            enqueueSnackbar("Something went wrong", {variant: "error"});
            console.error(e)
        }
    }

    async function handleTagSelectedPersonas() {
        enqueueSnackbar("Tagging selected personas", {variant: "info"});

        const newTagIds = tagsToApply.map(tag => Number(tag.id));
        let tagged = 0;
        let failed = 0;

        // Run one at a time: each update deletes and re-inserts the persona's tag links in a transaction,
        // and running them in parallel can deadlock in the database
        for (const persona of selectedPersonas ?? []) {
            // The API replaces all tag links when tagIds is sent, so merge with each persona's existing tags
            const existingTagIds = (persona.tagLinks ?? [])
                .map((link: any) => Number(link.tagRef?.id))
                .filter(id => !isNaN(id));

            try {
                await updatePersona(persona.id, {
                    tagIds: Array.from(new Set([...existingTagIds, ...newTagIds]))
                });
                tagged++;
            } catch (e) {
                failed++;
                console.error(`Failed to tag persona ${persona.id}`, e);
            }
        }

        if (failed === 0) {
            enqueueSnackbar(`Tags applied to ${tagged} personas`, {variant: "success"});
        } else {
            enqueueSnackbar(`Tagged ${tagged}, failed ${failed}`, {variant: "error"});
        }
        setTagDialogOpen(false);
        setTagsToApply([]);
        setSelectedPersonaIds([]);
        await fetchData();
    }

    return (<>
            <Header/>
            <div className="social new-persona-list-page">
                {!loading && !error && <PersonaSearchInput
                    tags={tags}
                    restore={restore}
                    toggleRestore={() => setRestore(prevState => !prevState)}
                    handlePersonaNew={handlePersonaNew}
                    search={search}
                    updateSearch={updateSearch}
                    perms={!user?.perms}
                    companyFilter={companyFilter}
                    onCompanyFilterChange={setCompanyFilter}
                    companyOptions={companyOptions}
                    showAllCompanies={showAllCompanies}
                    onShowAllCompaniesChange={setShowAllCompanies}
                    isAdmin={!!user?.perms.admin}
                />}
                {loading && <div className="new-persona-loading">
                    <LinearProgress/>
                </div>}
                {error && <div className="new-persona-error">Error: {error}</div>}
                {!loading && !error && <div className="new-persona-list-box">
                    <div className="new-persona-table-shell">
                        {fetching && <LinearProgress className="new-persona-fetching"/>}
                        <TableContainer component={Paper} className='profiles-container new-persona-table-container'>
                            <Table stickyHeader>
                                <TableHead className='table_head'>
                                    <TableRow>
                                        <TableCell className="table_cell top_row">
                                            <div style={{display: "flex", alignItems: "center"}}>
                                                <div className="table_cell top_row">
                                                    <Checkbox className="table_cell top_row" checked={allSelected}
                                                              onChange={handleSelectAll}></Checkbox>
                                                </div>
                                                <Tooltip title={restore ? "Restore selected" : "Delete selected"} arrow>
                                                    <span>
                                                        <IconButton
                                                            className="new-persona-bulk-action"
                                                            size="small"
                                                            color="secondary"
                                                            disabled={(selectedPersonas?.length ?? 0) === 0}
                                                            onClick={restore ? handleRestoreSelectedPersonas : handleDeleteSelectedPersonas}
                                                        >
                                                            {restore ? <RestoreFromTrashIcon fontSize="small"/> : <DeleteIcon fontSize="small"/>}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                {!restore && <Tooltip title="Tag selected" arrow>
                                                    <span>
                                                        <IconButton
                                                            className="new-persona-bulk-action"
                                                            size="small"
                                                            color="secondary"
                                                            disabled={(selectedPersonas?.length ?? 0) === 0}
                                                            onClick={() => setTagDialogOpen(true)}
                                                        >
                                                            <LocalOfferIcon fontSize="small"/>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>}
                                            </div>
                                        </TableCell>
                                        <TableCell className="table_cell top_row"
                                                   style={{fontWeight: "600", width: "10%"}}>{renderSortLabel("id", "ID")}</TableCell>
                                        <TableCell className="table_cell top_row"
                                                   style={{fontWeight: "600", width: "30%"}}>{renderSortLabel("name", "Name")}</TableCell>
                                        <TableCell className="table_cell top_row"
                                                   style={{width: "20%", fontWeight: "600"}}>{renderSortLabel("profileName", "Profile Name")}</TableCell>
                                        <TableCell className="table_cell top_row"
                                                   style={{fontWeight: "600", width: "10%"}}>{renderSortLabel("type", "Type")}</TableCell>
                                        <TableCell className="table_cell top_row"
                                                   style={{fontWeight: "600", width: "15%"}}>{renderSortLabel("company", "Company")}</TableCell>
                                        <TableCell className="table_cell top_row" style={{
                                            fontWeight: "600",
                                            paddingRight: "2rem"
                                        }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {personas?.map((persona, index) => (<PersonaTableRow
                                            key={persona.uuid}
                                            persona={persona}
                                            restore={restore}
                                            selected={selectedPersonas?.includes(persona) ?? false}
                                            dark={index % 2 === 0}
                                            onPersonaView={handlePersonaView}
                                            onPersonaCopy={handlePersonaCopy}
                                            onPersonaSelect={handlePersonaSelect}
                                        />))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                    <div className="pagination-container new-persona-pagination">
                        <span>{total} personas</span>
                        <Pagination color="secondary" count={totalPages}
                                    page={page} onChange={(_, page) => setPage(page)}/>
                    </div>
                </div>}
            </div>
            <PersonaCreateDialog open={personaCreateDialogOpen} onOpenChange={setPersonaCreateDialogOpen}
                                 onPersonaCreate={() => fetchData()}/>
            <PersonaViewDialog personaUuid={persona?.uuid ?? ""} open={personaViewDialogOpen}
                               onOpenChange={setPersonaViewDialogOpen} personaType={persona?.type ?? "default"}/>
            <Dialog open={tagDialogOpen} onClose={() => setTagDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add tags to {selectedPersonas?.length ?? 0} selected personas</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        multiple
                        value={tagsToApply}
                        renderInput={(params) => (
                            <TextField
                                style={{marginTop: "10px"}}
                                variant="outlined"
                                {...params}
                                label="Tags"
                            />
                        )}
                        options={tagOptions}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        groupBy={(option) => option.firstLetter}
                        getOptionLabel={(option) => option.name}
                        onChange={(e, newValue) => setTagsToApply(newValue)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTagDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="secondary" disabled={tagsToApply.length === 0}
                            onClick={handleTagSelectedPersonas}>Apply</Button>
                </DialogActions>
            </Dialog>
        </>);
};
